import React, { ForwardedRef, ReactNode, forwardRef } from 'react';
import { DOMProps } from '@react-types/shared';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { Table, TableProps, TableBody } from '../Table';

import { MiniGridRow } from './Row/Row';
import { MiniGridHeaderRow } from './HeaderRow/HeaderRow';
import { MiniGridDraggableList } from './DraggableList/DraggableList';

import './MiniGrid.css';

const cls = cn('MiniGrid');

export interface MiniGridProps extends TestProps, DOMProps, Loggable, Pick<TableProps, 'cellSize'> {
    /**
     * Дочерние элементы
     * @expectedNode `MiniGridHeaderRow` | `MiniGridRow`
     */
    children?: ReactNode;

    /**
     * Названия классов для элемента
     */
    className?: string;
}

/**
 * Компонент для отрисовки минигрида с возможностью добавления новых строк при помощи кнопки 'Добавить'
 * Также есть возможность сортировки строк при помощи dragAndDrop
 *
 * Инструкция по определению в макете MiniGrid, Table, Grid
 * https://components.adv.yandex-team.ru/?path=/docs/docs-как-отличить-minigrid-table-grid--docs
 */
const MiniGrid = forwardRef(function MiniGrid(
    { children, className, logNode, 'data-testid': dataTestId = 'MiniGrid', cellSize = 'm' }: MiniGridProps,
    ref: ForwardedRef<HTMLTableElement>,
): React.ReactElement {
    const { LogProvider } = useLogger('MiniGrid', logNode);

    return (
        <LogProvider>
            <Table
                ref={ref}
                columnsWidth="max"
                cellSize={cellSize}
                borderRadius="m"
                className={cls('', [className])}
                data-testid={dataTestId}
            >
                <TableBody className={cls('tbody')}>{children}</TableBody>
            </Table>
        </LogProvider>
    );
});

export { MiniGrid, MiniGridRow, MiniGridHeaderRow, MiniGridDraggableList };
